import { useFormContext, Controller } from "react-hook-form";
import { Box, Button, FormHelperText, Typography } from "@mui/material";
import { useState } from "react";
import { WebShopIcon } from "@/src/components/WebShopIcon";

const maxFilesAmount = 20;
const maxFileSizeInMb = 7.5;

const maxFileSize = maxFileSizeInMb * 1024 * 1024;

const allowedTypes = [
  "text/plain",
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
  "video/mpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const allowedTypesNamesMapping = {
  "text/plain": "txt",
  "image/jpeg": "jpeg",
  "image/png": "png",
  "image/gif": "gif",
  "video/mp4": "mp4",
  "video/mpeg": "mpeg",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
};

export const FileInput = ({ name, label }: { name: string; label: string }) => {
  const { control, trigger, setValue, watch } = useFormContext();
  // const [files, setFiles] = useState<File[]>([]);
  const watchedFiles = watch(name) as File[];
  const files = watchedFiles || [];

  const validateFiles = (value: File[]) => {
    if (!value) {
      return true;
    }
    if (value.length > maxFilesAmount)
      return `Можна завантажити не більше ${maxFilesAmount} файлів`;

    if (!value.every((file: File) => allowedTypes.includes(file.type))) {
      return "Дозволені формати: текстові файли, pdf, фото (jpeg, png, gif) або відео (mp4, mpeg)";
    }

    const totalSize = value.reduce((sum, file) => sum + file.size, 0);
    console.log(value, totalSize);
    if (totalSize > maxFileSize) {
      return `Сумарний розмір файлів не повинен перевищувати ${maxFileSizeInMb} МБ`;
    }

    return true;
  };

  const deleteFile = (fileName: string) => {
    const newFiles = files.filter(({ name }, i) => name + i !== fileName);
    // setFiles(newFiles);
    setValue(name, newFiles);
    trigger(name);
  };

  const allowedTypesLabels = allowedTypes
    .map(
      (type) =>
        allowedTypesNamesMapping[type as keyof typeof allowedTypesNamesMapping],
    )
    .join(", ");

  return (
    <Controller
      control={control}
      name={name}
      rules={{ validate: validateFiles }}
      render={({ field: { onChange }, fieldState: { error } }) => (
        <Box>
          <Typography className="mb-1 md:text-white">
            Додати вкладення (до {maxFilesAmount} файлів, загальний об’єм до{" "}
            {maxFileSizeInMb} МБ, формати: {allowedTypesLabels}):
          </Typography>
          <Button
            variant="contained"
            component="label"
            className="flex w-fit items-center justify-center gap-1"
          >
            <WebShopIcon name="save" strokeWidth={2} />
            {label}
            <input
              type="file"
              hidden
              multiple
              onChange={(e) => {
                const selectedFiles = Array.from(e.target.files || []);
                const newSelectedFiles = [...files, ...selectedFiles];
                onChange(newSelectedFiles);
                trigger(name);
              }}
              accept={allowedTypes.join(", ")}
              max={maxFilesAmount}
            />
          </Button>
          {files.length > 0 && (
            <Box mt={2}>
              {files.map((file, index) => (
                <div
                  className="flex items-center gap-0.5"
                  key={file.name + index}
                >
                  <Typography className="max-w-[93%] truncate text-xs md:text-white">
                    {file.name}
                  </Typography>
                  <div
                    className="h-4 w-4 rotate-45 cursor-pointer"
                    onClick={() => deleteFile(file.name + index)}
                  >
                    <WebShopIcon className="size-4 fill-blue" name="plus" />
                  </div>
                </div>
              ))}
            </Box>
          )}
          {error && <FormHelperText error> {error.message}</FormHelperText>}
        </Box>
      )}
    />
  );
};
